import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import SignatureCanvas from "react-signature-canvas";
import { LuCircleCheck, LuFileText, LuPenLine, LuX } from "react-icons/lu";
import { appConstants } from "utils/appConstants";
import SuccessCheckmark from "components/SuccessCheckmark/SuccessCheckmark";
import QuotationDocument from "./QuotationDocument";
import "./quotation.css";
import "./customerQuotation.css";

export default function CustomerQuotationReview() {
  const { id: quotationID } = useParams();
  const { search } = useLocation();
  const customerToken = new URLSearchParams(search).get("token") || "";
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [signing, setSigning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signatureError, setSignatureError] = useState("");
  const [closed, setClosed] = useState(false);
  const [animateSuccess, setAnimateSuccess] = useState(false);
  const signatureRef = useRef(null);
  const approvalInFlightRef = useRef(false);
  const reviewRequestRef = useRef(0);
  const successRef = useRef(null);
  const closedRef = useRef(null);

  useEffect(() => {
    if (closed) closedRef.current?.focus();
  }, [closed]);

  useEffect(() => {
    let active = true;
    reviewRequestRef.current += 1;
    approvalInFlightRef.current = false;
    const request = new AbortController();
    setLoading(true);
    setError("");
    setQuotation(null);
    setClosed(false);
    setSigning(false);
    setSaving(false);
    setAnimateSuccess(false);
    axios
      .post(
        `${appConstants.baseURL}quotation/fetchPendingQuotationForCustomer`,
        { quotationID, customerToken },
        { signal: request.signal, timeout: 30000 }
      )
      .then(({ data }) => {
        if (!active) return;
        if (!data?.Result_Status || !data.Result_Output?.[0])
          throw new Error(
            data?.Result_Message || "The quotation could not be loaded."
          );
        setQuotation(data.Result_Output[0]);
      })
      .catch((failure) => {
        if (active)
          setError(
            failure.response?.data?.Result_Message ||
              failure.message ||
              "The quotation could not be loaded."
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      reviewRequestRef.current += 1;
      request.abort();
    };
  }, [quotationID, customerToken]);

  const prepareSignature = () => {
    const pad = signatureRef.current;
    if (!pad) return;
    const canvas = pad.getCanvas();
    const bounds = canvas.getBoundingClientRect();
    const ratio = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
    canvas.width = Math.round(bounds.width * ratio);
    canvas.height = Math.round(bounds.height * ratio);
    canvas.getContext("2d").scale(ratio, ratio);
    pad.clear();
    setHasSignature(false);
    setSignatureError("");
  };

  const approve = async () => {
    if (approvalInFlightRef.current || !canApprove) return;
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setSignatureError("Please sign in the box before approving.");
      return;
    }
    approvalInFlightRef.current = true;
    const reviewRequest = reviewRequestRef.current;
    setSaving(true);
    setSignatureError("");
    try {
      const data = new FormData();
      data.append("quotationID", quotationID);
      data.append("customerToken", customerToken);
      if (!customerToken)
        data.append("signatureKey", appConstants.SIGNATURE_KEY);
      data.append(
        "imageData",
        signatureRef.current.getCanvas().toDataURL("image/png")
      );
      const response = await axios.post(
        `${appConstants.baseURL}quotation/approvedQuotationByCustomer`,
        data,
        { timeout: 30000 }
      );
      if (reviewRequestRef.current !== reviewRequest) return;
      const saved = response.data?.Result_Output;
      if (!response.data?.Result_Status || saved?.quotationStatus !== "Approved")
        throw new Error(
          response.data?.Result_Message ||
            "The quotation could not be approved."
        );
      setQuotation((previous) => ({
        ...previous,
        ...saved,
      }));
      const signedSuccessfully = Boolean(
        saved.signatureUploadedDate &&
        !Number.isNaN(new Date(saved.signatureUploadedDate).getTime())
      );
      setAnimateSuccess(signedSuccessfully);
      setSigning(false);
    } catch (failure) {
      if (reviewRequestRef.current !== reviewRequest) return;
      setSignatureError(
        failure.response?.data?.Result_Message ||
          failure.message ||
          "Approval could not be confirmed. Reopen the email link before trying again."
      );
    } finally {
      if (reviewRequestRef.current === reviewRequest) {
        approvalInFlightRef.current = false;
        setSaving(false);
      }
    }
  };

  const approved = quotation?.quotationStatus === "Approved";
  const completionDate = quotation?.signatureUploadedDate
    ? new Date(quotation.signatureUploadedDate) : null;
  const signed = approved && completionDate && !Number.isNaN(completionDate.getTime());
  const canApprove =
    quotation &&
    !approved &&
    !quotation.expiryDate &&
    ["Pending", "Sent", "Viewed"].includes(quotation.quotationStatus);
  const pdfUrl = `${
    appConstants.baseURL
  }quotation/customerPdf/${quotationID}?token=${encodeURIComponent(
    customerToken
  )}`;
  const documentPreview = quotation && (
    <QuotationDocument
      values={quotation}
      items={quotation.itemsList || []}
      quotationID={quotationID}
      organisation={quotation.organisation}
      quoteNumber={quotation.quotationNumber}
      currency={quotation.currencyValue || "AUD"}
      customer={quotation.customerCompanyName || "Customer"}
      validity={quotation.validUpto || "14 days"}
    />
  );

  return (
    <main className="customer-quotation-page" data-theme="light">
      {loading && (
        <div
          className="customer-quotation-loading-bar"
          role="progressbar"
          aria-label="Loading quotation"
        />
      )}
      <header className="customer-quotation-brand">
        <span className="customer-quotation-brand-mark">ir</span>
        <strong>invoice &amp; recover</strong>
        <span className="customer-quotation-brand-caption">Quotation review</span>
      </header>
      {loading ? null : error ? (
        <section className="customer-quotation-message" role="alert">
          <h1>Quotation unavailable</h1>
          <p>{error}</p>
        </section>
      ) : closed ? (
        <section
          className="customer-quotation-message customer-quotation-closed"
          role="status"
          aria-labelledby="customer-quotation-closed-title"
          tabIndex={-1}
          ref={closedRef}
        >
          {signed ? <SuccessCheckmark /> : (
            <span className="customer-quotation-closed-icon"><LuFileText aria-hidden="true" /></span>
          )}
          <span className="customer-quotation-closed-number">Quotation {quotation?.quotationNumber}</span>
          <h1 id="customer-quotation-closed-title">
            {approved ? "Thank you for approving" : "Quotation closed"}
          </h1>
          {signed && <p>Your approval and signature have been saved successfully.</p>}
          <p>You can now close this browser tab.</p>
        </section>
      ) : (
        quotation && (
          <>
            <section className="customer-quotation-heading">
              <div>
                <p>{quotation.organisation}</p>
                <h1>Quotation {quotation.quotationNumber}</h1>
                <span>Prepared for {quotation.customerCompanyName}</span>
              </div>
              <span
                className={`customer-quotation-status${
                  approved ? " is-approved" : ""
                }`}
              >
                {approved
                  ? "Approved"
                  : quotation.expiryDate
                  ? "Expired"
                  : "Awaiting approval"}
              </span>
            </section>
            {approved && (
              <section
                className={`customer-quotation-success${animateSuccess ? " is-animated" : ""}`}
                role="status"
                aria-live="polite"
                aria-atomic="true"
                tabIndex={-1}
                ref={successRef}
              >
                <SuccessCheckmark
                  animate={animateSuccess}
                  pulse={Boolean(signed)}
                  onComplete={() => setAnimateSuccess(false)}
                />
                <div className="customer-quotation-success-copy">
                  <h2>
                    {signed ? "Quotation approved & signed successfully!" : "Your quotation is approved."}
                  </h2>
                  <p>
                    {signed
                      ? "Your approval and signature have been recorded successfully."
                      : "Thank you for reviewing this quotation."}
                  </p>
                  <dl>
                    <div><dt>Quotation</dt><dd>{quotation.quotationNumber}</dd></div>
                    {quotation.customerCompanyName && (
                      <div><dt>Recipient</dt><dd>{quotation.customerCompanyName}</dd></div>
                    )}
                    {signed && (
                      <div>
                        <dt>Completed</dt>
                        <dd>
                          <time dateTime={completionDate.toISOString()}>
                            {completionDate.toLocaleString("en-AU", {
                              year: "numeric", month: "short", day: "numeric",
                              hour: "2-digit", minute: "2-digit", timeZoneName: "short",
                            })}
                          </time>
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </section>
            )}
            {quotation.expiryDate && !approved && (
              <p className="customer-quotation-error" role="alert">
                This quotation has expired. Please ask the sender for an updated
                quotation.
              </p>
            )}
            <div className={`customer-quotation-review-layout${signed ? " is-complete" : ""}`}>
              {!signed && (
                <section
                  className="customer-quotation-document"
                  aria-label={`Quotation ${quotation.quotationNumber} preview`}
                >
                  {documentPreview}
                </section>
              )}
              <aside className="customer-quotation-actions" aria-label="Quotation actions">
                <div className="customer-quotation-actions-intro">
                  {!signed && (
                    <span className="customer-quotation-action-icon" aria-hidden="true">
                      <LuPenLine />
                    </span>
                  )}
                  <h2>Review quotation</h2>
                  <p>
                    {approved
                      ? signed ? "Your signature and approval have been saved." : "This quotation has been approved."
                      : "Review the quotation and terms, then add your signature to confirm your approval."}
                  </p>
                </div>
                {!signed && (
                  <button
                    type="button"
                    className="is-primary"
                    disabled={!canApprove}
                    onClick={() => setSigning(true)}
                  >
                    <LuPenLine />
                    Sign &amp; Approve
                  </button>
                )}
                <button type="button" onClick={() => {
                  setAnimateSuccess(false);
                  setClosed(true);
                }}>
                  Close
                </button>
                <a
                  className="customer-quotation-pdf-button"
                  href={pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <LuFileText />
                  View PDF
                </a>
                {canApprove && (
                  <p className="customer-quotation-approval-note">
                    <LuCircleCheck aria-hidden="true" />
                    <span>No payment is taken when you approve.</span>
                  </p>
                )}
              </aside>
            </div>
          </>
        )
      )}
      <Dialog
        open={signing}
        onClose={saving ? undefined : () => setSigning(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="customer-signature-title"
        PaperProps={{
          className: "customer-quotation-signature-dialog",
          "data-theme": "light",
        }}
        TransitionProps={{
          onEntering: prepareSignature,
          onExited: () => {
            successRef.current?.focus({ preventScroll: true });
            successRef.current?.scrollIntoView?.({ block: "center", behavior: "auto" });
          },
        }}
      >
        <header>
          <h2 id="customer-signature-title">Sign &amp; approve quotation</h2>
          <button
            type="button"
            aria-label="Close signature"
            disabled={saving}
            onClick={() => setSigning(false)}
          >
            <LuX />
          </button>
        </header>
        <div className="customer-quotation-signature-body">
          <div className="customer-quotation-signature-pad">
            <SignatureCanvas
              ref={signatureRef}
              penColor="#142624"
              backgroundColor="#ffffff"
              minWidth={1.2}
              maxWidth={1.2}
              dotSize={1.2}
              minDistance={1.5}
              throttle={8}
              canvasProps={{
                className: "customer-quotation-signature-canvas",
                "aria-label": "Draw your signature",
              }}
              onBegin={() => setSignatureError("")}
              onEnd={() => setHasSignature(!signatureRef.current?.isEmpty())}
            />
          </div>
          <button
            type="button"
            className="customer-quotation-clear-signature"
            disabled={saving}
            onClick={() => {
              signatureRef.current?.clear();
              setHasSignature(false);
              setSignatureError("");
            }}
          >
            Clear signature
          </button>
          <p>
            By approving, you agree to the work and terms in quotation{" "}
            {quotation?.quotationNumber}.
          </p>
          {signatureError && (
            <p className="customer-quotation-error" role="alert">
              {signatureError}
            </p>
          )}
        </div>
        <footer>
          <button
            type="button"
            disabled={saving}
            onClick={() => setSigning(false)}
          >
            Close
          </button>
          <button
            type="button"
            className="is-primary"
            disabled={saving || !hasSignature}
            onClick={approve}
          >
            {saving ? "Approving…" : "Approve"}
          </button>
        </footer>
      </Dialog>
    </main>
  );
}
